import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Drawer,
  IconButton,
  Divider,
  Stack,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import PaneHeader from './PaneHeader';
import { useDispatch, useSelector } from 'react-redux';
import { createBowtie, updateBowtie, setViewable } from '../../features/bowtie/bowtieSlice';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import BowtieIconOrange from '../icons/BowtieIconOrange';

const BowtieEditSidePanel = ({
  isOpen,
  readOnly = false,
  lockedByName = null,
  onActivity = null,
  idleWarningSeconds = null,
  onClose,
  bowtie = null,
  rightOffset = 0,
  isActive = false,
  openPanes = [],
  activePaneType,
  onSwitchPane,
  onDirtyChange,
  showCloseWarning = false,
  onCloseWarningDismiss,
  onDiscard,
  discardLabel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Read viewable live from Redux so the switch reflects changes immediately
  const viewable = useSelector((state) => state.bowtie.data?.viewable ?? false)

  // Which workspace a newly-created bowtie should land in.
  const activeWorkspace = useSelector((state) => state.org?.activeWorkspace || 'personal')
  const activeOrgName = useSelector(
    (state) => state.org?.myOrgs?.find((o) => o.slug === state.org.activeWorkspace)?.name
  )

  // Determine if we're creating or editing
  const isCreating = !bowtie;
  const isEditing = !!bowtie;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isDirty =
    formData.title !== (bowtie?.title ?? '') ||
    formData.description !== (bowtie?.description ?? '') ||
    formData.event !== (bowtie?.event ?? '');

  useEffect(() => {
    const id = requestAnimationFrame(() => onDirtyChange?.(isDirty));
    return () => cancelAnimationFrame(id);
  }, [isDirty, onDirtyChange]);

  // ✅ CONSTANTS: Consistent styling
  const bowtieColor = '#E67E22'; // Orange color for bowtie

  // ✅ Initialize form when bowtie changes
  useEffect(() => {
    if (isEditing && bowtie) {
      setFormData({
        title: bowtie.title || '',
        description: bowtie.description || '',
        event: bowtie.event || ''
      });
    } else {
      // Reset to defaults for create mode
      setFormData({ title: '', description: '', event: '' });
    }
    setErrors({});
  }, [bowtie, isEditing]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.event.trim()) newErrors.event = 'Hazardous event is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      if (isCreating) {
        // Create new bowtie — in the active workspace (personal or an org)
        const payload =
          activeWorkspace && activeWorkspace !== 'personal'
            ? { ...formData, organisation: activeWorkspace }
            : formData;
        const result = await dispatch(createBowtie(payload));

        if (createBowtie.fulfilled.match(result)) {
          const createdBowtie = result.payload;
          navigate('/editor/' + createdBowtie._id);
          toast.success('Bowtie created successfully!');
          handleClose();
        } else {
          setErrors({ submit: result.payload || 'Failed to create bowtie' });
        }
      } else {
        // Update existing bowtie
        const updatedData = {
            _id: bowtie._id,
            title: formData.title,
            description: formData.description,
            event: formData.event,
          };
        const result = await dispatch(updateBowtie(updatedData));

        if (updateBowtie.fulfilled.match(result)) {
          handleClose();
        } else {
          setErrors({ submit: result.payload || 'Failed to update bowtie' });
        }
      }
    } catch (error) {
      console.error('Error saving bowtie:', error);
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({ title: '', description: '', event: '' });
    setErrors({});
    setSaving(false);
    onClose();
  };

  const handleViewableToggle = useCallback(async (event) => {
    if (!bowtie?._id) return;
    const newValue = event.target.checked;
    try {
      await dispatch(setViewable({ id: bowtie._id, viewable: newValue })).unwrap();
    } catch (err) {
      toast.error('Failed to update sharing settings');
    }
  }, [bowtie, dispatch]);

  const handleCopyViewerLink = useCallback(() => {
    const url = `${window.location.origin}/viewer/${bowtie?._id}?utm_source=viewer_share`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied to clipboard');
    }).catch(() => {
      toast.error('Could not copy link');
    });
  }, [bowtie]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          zIndex: isActive ? 1202 : 1201,
          width: 400,
          right: `${rightOffset}px`,
          padding: 0,
          border: 'none',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PaneHeader
          headerColor={bowtieColor}
          icon={<BowtieIconOrange sx={{ color: 'white', fontSize: 24 }} />}
          title={`${isCreating ? 'Add' : 'Edit'} Bowtie`}
          onClose={handleClose}
          isDirty={isDirty}
          showCloseWarning={showCloseWarning}
          onCloseWarningDismiss={onCloseWarningDismiss}
          onDiscard={onDiscard}
          discardLabel={discardLabel}
          openPanes={openPanes}
          activePaneType={activePaneType}
          onSwitchPane={onSwitchPane}
        />

        {/* ✅ FORM CONTENT - Consistent with other panels */}
        <Box
          onKeyDown={onActivity || undefined}
          onMouseMove={onActivity || undefined}
          sx={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {readOnly && (
            <Alert severity="info" sx={{ marginBottom: 2 }}>
              {lockedByName ? `${lockedByName} is` : 'Someone else is'} currently editing this bowtie. You can view it, but not make changes.
            </Alert>
          )}

          {idleWarningSeconds != null && (
            <Alert
              severity="warning"
              sx={{ marginBottom: 2 }}
              action={
                <Button color="inherit" size="small" onClick={onActivity}>
                  Still editing
                </Button>
              }
            >
              Still there? This becomes editable by others in {idleWarningSeconds}s.
            </Alert>
          )}

          {/* Error Alert */}
          {errors.submit && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Which workspace this new bowtie will belong to */}
          {isCreating && activeWorkspace !== 'personal' && (
            <Alert severity="info" sx={{ marginBottom: 1 }}>
              This bowtie will be created in <strong>{activeOrgName || activeWorkspace}</strong> and
              shared with everyone in that organisation.
            </Alert>
          )}

          {/* ✅ UPDATED: Title Field - Clean styling like ThreatEditSidePanel */}
          <TextField
            label="Bowtie Title"
            value={formData.title}
            onChange={handleInputChange('title')}
            error={!!errors.title}
            helperText={errors.title || 'Give your bowtie analysis a clear, descriptive title'}
            fullWidth
            autoFocus
            placeholder="e.g., Workplace Safety Risk Analysis"
            required
            disabled={saving || readOnly}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: bowtieColor
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: bowtieColor
              },
              '& .MuiInputBase-input': {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }
            }}
          />

          {/* ✅ UPDATED: Event Field - Clean styling like ThreatEditSidePanel */}
          <TextField
            label="Hazardous Event"
            value={formData.event}
            onChange={handleInputChange('event')}
            error={!!errors.event}
            helperText={errors.event || 'Describe the central hazardous event being analyzed'}
            fullWidth
            placeholder="e.g., Worker Injury During Equipment Operation"
            required
            disabled={saving || readOnly}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: bowtieColor
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: bowtieColor
              },
              '& .MuiInputBase-input': {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }
            }}
          />

          {/* ✅ UPDATED: Description Field - Clean styling like ThreatEditSidePanel */}
          <TextField
            label="Description (Optional)"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleInputChange('description')}
            placeholder="Provide additional context, scope, or background information for this bowtie analysis..."
            fullWidth
            disabled={saving || readOnly}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: bowtieColor
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: bowtieColor
              }
            }}
          />

          {/* ✅ Creation/Update info - only show for existing bowties */}
          {isEditing && bowtie?.createdAt && (
            <Typography variant="caption" color="text.secondary">
              Created: {new Date(bowtie.createdAt).toLocaleDateString()}
            </Typography>
          )}

          {/* Total likelihood display */}
          {isEditing && typeof bowtie?.threatSum === 'number' && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '10px 12px',
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ffcc80'
            }}>
              <Typography variant="body2" color="text.secondary">
                Total Likelihood:
              </Typography>
              <Typography variant="body2" fontWeight={600} color="#E67E22">
                {bowtie.threatSum.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Sharing card — only shown in edit mode */}
          {isEditing && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>

              {/* Status + toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: viewable ? 1.5 : 2 }}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {viewable ? 'Publicly viewable' : 'Private'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewable ? 'Anyone with the link can view this bowtie' : 'Only you can see this bowtie'}
                  </Typography>
                </Box>
                <Switch
                  checked={viewable}
                  onChange={handleViewableToggle}
                  size="small"
                  sx={{
                    ml: 2,
                    flexShrink: 0,
                    '& .MuiSwitch-switchBase.Mui-checked': { color: bowtieColor },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: bowtieColor },
                  }}
                />
              </Box>

              {/* Viewer URL — only when viewable */}
              {viewable && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      fontFamily: 'monospace',
                      bgcolor: 'grey.100',
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'text.secondary',
                    }}
                  >
                    {`${window.location.origin}/viewer/${bowtie._id}`}
                  </Typography>
                  <IconButton size="small" onClick={handleCopyViewerLink} title="Copy link">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {/* ✅ Info alert */}
          
        </Box>

        <Divider />

        {/* ✅ ACTION BUTTONS - Consistent with other panels */}
        <Box sx={{ 
          padding: '16px 20px', 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          {/* No delete button for bowties in edit mode */}
          <Box />

          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={{ minWidth: '80px', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>

            {!readOnly && (
              <Button
                variant="contained"
                startIcon={saving ? null : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || (isEditing && !isDirty)}
                sx={{
                  backgroundColor: bowtieColor,
                  minWidth: '120px',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#D35400' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                {saving
                  ? (isCreating ? 'Creating...' : 'Updating...')
                  : (isCreating ? 'Create Bowtie' : 'Update')
                }
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BowtieEditSidePanel;